import { useBreakpoint, useClickOutside } from '@pharmac-extranet/shared'
import { PharmacIcon } from '@pharmac/ui'
import React, { useEffect, useRef, useState } from 'react'
import {
  ExtranetFiltersButton,
  ExtranetFiltersButtonIcon,
  ExtranetFiltersContainer,
  ExtranetFiltersContainerInner,
  ExtranetFiltersResetButton,
  ExtranetFiltersWrapper,
} from './ExtranetFiltersComponents'

type ExtranetFiltersElementArgs = {
  closeFilters: () => void
  resetFilters?: () => void
  hasActiveFilters?: boolean
}
type ExtranetFiltersElementRender =
  | (({
      closeFilters,
      resetFilters,
      hasActiveFilters,
    }: ExtranetFiltersElementArgs) => React.ReactNode)
  | React.ReactNode
  | undefined

type Props = {
  title: string
  render: ExtranetFiltersElementRender
  resetFilters?: () => void
  hasActiveFilters?: boolean
  disableOutsideClickHandler?: boolean
  activeFilterCount?: number
}

export const ExtranetFilters = ({
  title,
  hasActiveFilters,
  disableOutsideClickHandler,
  resetFilters,
  render,
  activeFilterCount,
}: Props) => {
  const [buttonPosition, setButtonPosition] = useState<DOMRect | undefined>()
  const [isOpen, setIsOpen] = useState(false)
  const { maxTablet } = useBreakpoint()

  const [wrapperRef] = useClickOutside(() => setIsOpen(false))

  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const closeFilters = () => setIsOpen(false)

  const childrenProps = {
    resetFilters: () => (!!resetFilters ? resetFilters() : null),
    closeFilters: () => (!!closeFilters ? closeFilters() : null),
    hasActiveFilters: !!hasActiveFilters,
  }

  useEffect(() => {
    if (buttonRef && buttonRef.current) {
      setButtonPosition(buttonRef.current.getBoundingClientRect())
    }
  }, [buttonRef])

  return (
    <ExtranetFiltersWrapper
      className="enfilters__wrapper"
      ref={!disableOutsideClickHandler ? wrapperRef : undefined}
    >
      <ExtranetFiltersButton
        className="enfilters__button"
        ref={buttonRef}
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <ExtranetFiltersButtonIcon className="enfilters__button--icon">
          <PharmacIcon icon="filter" />
        </ExtranetFiltersButtonIcon>
        <div className="enfilters__button--text">{title}</div>
        {!maxTablet && (
          <>
            {hasActiveFilters && (
              <>
                <div style={{ width: 'var(--spacing-small)' }} />
                <div className="enfilters__button--count">({activeFilterCount})</div>
                <div style={{ width: 'var(--spacing-small)' }} />
              </>
            )}
          </>
        )}
        {hasActiveFilters && maxTablet && (
          <div className="enfilters__button--active-badge">{activeFilterCount}</div>
        )}
      </ExtranetFiltersButton>
      {!!hasActiveFilters && (
        <ExtranetFiltersResetButton
          className="enfilters__reset-button"
          onClick={resetFilters}
        >
          <PharmacIcon
            icon="x"
            size="22"
          />
        </ExtranetFiltersResetButton>
      )}
      <ExtranetFiltersContainer
        className="enfilters enfilters__container"
        isOpen={isOpen}
        domRect={buttonPosition}
      >
        <ExtranetFiltersContainerInner className="enfilters__container--inner">
          {typeof render === 'function' ? render(childrenProps) : render}
        </ExtranetFiltersContainerInner>
      </ExtranetFiltersContainer>
    </ExtranetFiltersWrapper>
  )
}
